/**
 * GiftCardLookupPage.jsx
 *
 * Recipient lost their email? Look up by code + recipient email.
 * Strictly rate-limited backend (5/min) prevents brute-force enumeration.
 */
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Search, ChevronLeft, Loader2, ShieldCheck } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function GiftCardLookupPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim() || !email.trim()) {
      toast.error("Both code and email are required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/v2/public/gift-card-lookup`, {
        code: code.trim().toUpperCase(),
        recipient_email: email.trim().toLowerCase(),
      });
      if (res.data?.view_token) {
        navigate(`/gift-card/view/${res.data.view_token}`);
      }
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("Too many attempts. Please wait a few minutes and try again.");
      } else {
        // Generic — don't leak whether code exists
        toast.error("We couldn't find a gift card matching that code and email.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white" data-testid="gift-card-lookup-page">
      <nav className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-gray-100 pt-[env(safe-area-inset-top)]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-gray-100 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <Link to="/" className="text-sm font-medium text-gray-700 hover:text-gray-900">Home</Link>
        </div>
      </nav>

      <main className="max-w-md mx-auto px-4 sm:px-6 pt-12 pb-16">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-600 shadow-md mb-4">
            <Gift className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Find your gift card</h1>
          <p className="text-gray-600">Enter your gift card code and the email it was sent to.</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1.5">Gift card code</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ABCD-XXXX-XXXX-WXYZ"
              className="w-full px-3 py-2.5 rounded-xl border-2 border-gray-200 focus:outline-none focus:border-blue-500 transition-colors font-mono tracking-wider"
              data-testid="gift-card-lookup-code"
              autoComplete="off"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1.5">Recipient email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-3 py-2.5 rounded-xl border-2 border-gray-200 focus:outline-none focus:border-blue-500 transition-colors"
              data-testid="gift-card-lookup-email"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:opacity-50 transition-colors"
            data-testid="gift-card-lookup-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {submitting ? "Looking up…" : "Find my gift card"}
          </button>
          <p className="text-center text-xs text-gray-500 flex items-center justify-center gap-1.5 pt-2">
            <ShieldCheck className="w-3 h-3" />
            Both code and email must match — protected against brute force.
          </p>
        </form>
      </main>
    </div>
  );
}
